import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "src/common/state/hooks";
import { getConnectedDevice } from "../state/deviceInstallerSlice";
import type { ScreenMirrorSession } from "../utils/screenMirror";
import { startScreenMirror } from "../utils/screenMirror";
import { CloseIcon, PlayIcon, Spinner } from "./icons";
import Badge from "../../../common/components/Badge/Badge";

type MirrorStatus = "idle" | "starting" | "streaming" | "error";

const ScreenMirror = () => {
  const { t } = useTranslation();
  const device = useAppSelector(getConnectedDevice);
  const isReady = device?.status === "ready";

  const [status, setStatus] = useState<MirrorStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [isStereo, setIsStereo] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<ScreenMirrorSession | null>(null);

  const stopMirror = useCallback(async () => {
    if (sessionRef.current) {
      await sessionRef.current.stop();
      sessionRef.current = null;
    }
    if (containerRef.current) {
      containerRef.current.innerHTML = "";
    }
    setStatus("idle");
  }, []);

  const startMirror = useCallback(async () => {
    if (!device) return;
    setStatus("starting");
    setError(null);

    try {
      const session = await startScreenMirror(device.productName);
      sessionRef.current = session;
      setIsStereo(session.isStereo);

      const canvas = session.canvas;
      canvas.style.display = "block";
      if (session.cssRotateDeg) {
        // Scale up to cover the corners exposed by rotation
        const absAngle = Math.abs(session.cssRotateDeg) * (Math.PI / 180);
        const scale = Math.cos(absAngle) + Math.sin(absAngle);
        canvas.style.transform = `rotate(${session.cssRotateDeg}deg) scale(${scale.toFixed(3)})`;
        // Rotate around the center of the left eye (25% of canvas = 50% of visible area)
        canvas.style.transformOrigin = "25% 50%";
      }
      if (session.isStereo) {
        // Raw stereo frame (2 eyes side by side, no crop). Show only the left eye
        // by doubling width inside an overflow:hidden container.
        canvas.style.width = "200%";
        canvas.style.height = "auto";
      } else if (session.isVR) {
        // VR with crop: stream is already cropped to one eye, fit in container
        canvas.style.height = "100%";
        canvas.style.width = "auto";
      } else {
        // Mobile / non-VR: fill the fixed-height container, keep aspect ratio
        canvas.style.height = "68vh";
        canvas.style.width = "auto";
        canvas.style.maxWidth = "100%";
      }

      if (containerRef.current) {
        containerRef.current.innerHTML = "";
        containerRef.current.appendChild(canvas);
      }

      setStatus("streaming");
    } catch (err: any) {
      setStatus("error");
      setError(err?.message || "Unknown error");
      sessionRef.current = null;
    }
  }, [device]);

  // Auto-stop when device disconnects
  useEffect(() => {
    if (!isReady && sessionRef.current) {
      void stopMirror();
    }
  }, [isReady, stopMirror]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        void sessionRef.current.stop();
        sessionRef.current = null;
      }
    };
  }, []);

  if (!isReady) return null;

  const isStreaming = status === "streaming" || status === "starting";

  return (
    <div className="mt-6 mb-6">
      {/* Header + button row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-800">{t("screenMirror.title")}</h2>
          <span className="relative inline-block group">
            <Badge
              color="bg-blue-100"
              textColor="text-blue-700"
              text="BETA"
              className="text-[10px] cursor-help"
            />
            <div className="absolute left-0 top-full mt-1 z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none">
              <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-72 shadow-lg whitespace-normal font-normal">
                {t("screenMirror.betaNotice")}
              </div>
            </div>
          </span>
        </div>
        {isStreaming && (
          <div className="flex items-center gap-2">
            {status === "starting" && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-gray-50 text-gray-500 border border-gray-200">
                <Spinner className="w-4 h-4" color="green" />
                {t("screenMirror.starting")}
              </span>
            )}
            {status === "streaming" && (
              <button
                onClick={stopMirror}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-colors"
              >
                <CloseIcon className="w-4 h-4" />
                {t("screenMirror.stop")}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Description + centered start button when idle */}
      {status === "idle" && (
        <div className="flex flex-col items-center gap-3 py-4">
          <p className="text-sm text-gray-500">{t("screenMirror.description")}</p>
          <button
            onClick={startMirror}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 transition-colors"
          >
            <PlayIcon className="w-4 h-4" />
            {t("screenMirror.start")}
          </button>
        </div>
      )}

      {/* Error + retry */}
      {status === "error" && (
        <div className="flex flex-col items-center gap-3 py-4">
          {error && (
            <div className="flex items-start gap-2 bg-red-50 rounded-lg p-3">
              <p className="text-sm text-red-600">{t("screenMirror.error", { error })}</p>
            </div>
          )}
          <button
            onClick={startMirror}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200 transition-colors"
          >
            <PlayIcon className="w-4 h-4" />
            {t("screenMirror.retry")}
          </button>
        </div>
      )}

      {/* Canvas container — fixed height for all devices, overflow:hidden crops VR right eye */}
      <div
        ref={containerRef}
        className={
          status === "streaming"
            ? `bg-black rounded-lg overflow-hidden border border-gray-200 h-[68vh]${
                !isStereo ? " flex items-center justify-center" : ""
              }`
            : ""
        }
      />

      {/* Streaming indicator */}
      {status === "streaming" && (
        <div className="flex items-center gap-2 mt-2">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-xs text-gray-500">{t("screenMirror.live")}</span>
        </div>
      )}
    </div>
  );
};

export default ScreenMirror;
